export enum ImageFormat {
  Jpeg = 'jpeg',
  Png = 'png',
  WebP = 'webp',
  Gif = 'gif',
  Bmp = 'bmp',
  Avif = 'avif',
  Heic = 'heic',
  Tiff = 'tiff',
  Raw = 'raw',
  Psd = 'psd',
  Svg = 'svg',
}

export enum FormatSupportKind {
  Native = 'native',
  NativeOrWasm = 'native-or-wasm',
  FirstFrame = 'first-frame',
  Unsupported = 'unsupported',
}

export interface FormatSupport {
  kind: FormatSupportKind
  label: string
  mimeType: string
  extensions: readonly string[]
  hint: string | null
}

const CONVERT_TO_RASTER_HINT = 'Export it as JPEG or PNG first.'

export const FORMAT_SUPPORT: Readonly<Record<ImageFormat, FormatSupport>> = {
  [ImageFormat.Jpeg]: { kind: FormatSupportKind.Native, label: 'JPEG', mimeType: 'image/jpeg', extensions: ['.jpg', '.jpeg', '.jfif'], hint: null },
  [ImageFormat.Png]: { kind: FormatSupportKind.Native, label: 'PNG', mimeType: 'image/png', extensions: ['.png'], hint: null },
  [ImageFormat.WebP]: { kind: FormatSupportKind.Native, label: 'WebP', mimeType: 'image/webp', extensions: ['.webp'], hint: null },
  [ImageFormat.Gif]: { kind: FormatSupportKind.FirstFrame, label: 'GIF', mimeType: 'image/gif', extensions: ['.gif'], hint: null },
  [ImageFormat.Bmp]: { kind: FormatSupportKind.Native, label: 'BMP', mimeType: 'image/bmp', extensions: ['.bmp'], hint: null },
  [ImageFormat.Avif]: { kind: FormatSupportKind.Native, label: 'AVIF', mimeType: 'image/avif', extensions: ['.avif'], hint: null },
  [ImageFormat.Heic]: { kind: FormatSupportKind.NativeOrWasm, label: 'HEIC/HEIF', mimeType: 'image/heic', extensions: ['.heic', '.heif'], hint: null },
  [ImageFormat.Tiff]: { kind: FormatSupportKind.Unsupported, label: 'TIFF', mimeType: 'image/tiff', extensions: ['.tif', '.tiff'], hint: CONVERT_TO_RASTER_HINT },
  [ImageFormat.Raw]: { kind: FormatSupportKind.Unsupported, label: 'RAW', mimeType: 'image/x-raw', extensions: [], hint: CONVERT_TO_RASTER_HINT },
  [ImageFormat.Psd]: { kind: FormatSupportKind.Unsupported, label: 'PSD', mimeType: 'image/vnd.adobe.photoshop', extensions: ['.psd'], hint: CONVERT_TO_RASTER_HINT },
  [ImageFormat.Svg]: { kind: FormatSupportKind.Unsupported, label: 'SVG', mimeType: 'image/svg+xml', extensions: ['.svg'], hint: 'This tool works with photos, not vector files.' },
}

const SUPPORTED_FORMATS = Object.values(ImageFormat).filter(
  (format) => FORMAT_SUPPORT[format].kind !== FormatSupportKind.Unsupported,
)

export const IMAGE_FILE_ACCEPT = SUPPORTED_FORMATS.flatMap((format) => {
  const support = FORMAT_SUPPORT[format]
  return [support.mimeType, ...support.extensions]
}).join(',')

const HEIC_BRANDS = new Set(['heic', 'heix', 'hevc', 'hevx', 'mif1', 'msf1'])
const AVIF_BRANDS = new Set(['avif', 'avis'])

function matches(header: Uint8Array, offset: number, bytes: readonly number[]): boolean {
  return bytes.every((byte, index) => header[offset + index] === byte)
}

function ascii(header: Uint8Array, start: number, length: number): string {
  return String.fromCharCode(...header.slice(start, start + length))
}

function isoBaseMediaFormat(header: Uint8Array): ImageFormat | null {
  if (header.length < 12 || ascii(header, 4, 4) !== 'ftyp') return null
  for (let offset = 8; offset + 4 <= header.length; offset += 4) {
    const brand = ascii(header, offset, 4).toLowerCase()
    if (AVIF_BRANDS.has(brand)) return ImageFormat.Avif
    if (HEIC_BRANDS.has(brand)) return ImageFormat.Heic
  }
  return null
}

export function detectImageFormat(header: Uint8Array): ImageFormat | null {
  if (matches(header, 0, [0xff, 0xd8, 0xff])) return ImageFormat.Jpeg
  if (matches(header, 0, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])) return ImageFormat.Png
  if (ascii(header, 0, 4) === 'RIFF' && ascii(header, 8, 4) === 'WEBP') return ImageFormat.WebP
  const gif = ascii(header, 0, 6)
  if (gif === 'GIF87a' || gif === 'GIF89a') return ImageFormat.Gif
  if (ascii(header, 0, 2) === 'BM') return ImageFormat.Bmp
  if (ascii(header, 0, 4) === '8BPS') return ImageFormat.Psd

  const littleEndianTiff = matches(header, 0, [0x49, 0x49, 0x2a, 0x00])
  const bigEndianTiff = matches(header, 0, [0x4d, 0x4d, 0x00, 0x2a])
  if (littleEndianTiff && matches(header, 8, [0x43, 0x52, 0x02, 0x00])) return ImageFormat.Raw
  if (littleEndianTiff || bigEndianTiff) return ImageFormat.Tiff

  const mediaFormat = isoBaseMediaFormat(header)
  if (mediaFormat) return mediaFormat

  const text = ascii(header, 0, header.length).replace(/^\ufeff/, '').trimStart().toLowerCase()
  if (text.startsWith('<svg') || text.startsWith('<?xml')) return ImageFormat.Svg
  return null
}

export function usesFirstFrame(format: ImageFormat | null, header: Uint8Array): boolean {
  if (format === ImageFormat.Gif) return true
  if (format === ImageFormat.WebP) {
    return ascii(header, 12, 4) === 'VP8X' && ((header[20] ?? 0) & 0x02) !== 0
  }
  if (format === ImageFormat.Avif && ascii(header, 4, 4) === 'ftyp') {
    for (let offset = 8; offset + 4 <= header.length; offset += 4) {
      if (ascii(header, offset, 4).toLowerCase() === 'avis') return true
    }
  }
  return false
}

export function validateFormatSupport(): void {
  const formats = Object.values(ImageFormat)
  const configured = Object.keys(FORMAT_SUPPORT)
  if (configured.length !== formats.length || formats.some((format) => !FORMAT_SUPPORT[format])) {
    throw new Error('Image format support must configure every ImageFormat exactly once')
  }
  for (const [format, support] of Object.entries(FORMAT_SUPPORT)) {
    if (!Object.values(FormatSupportKind).includes(support.kind)) {
      throw new Error(`Image format "${format}" has an invalid support kind`)
    }
    if (!support.label.trim() || !support.mimeType.trim()) {
      throw new Error(`Image format "${format}" requires a label and MIME type`)
    }
    if (support.kind === FormatSupportKind.Unsupported && !support.hint?.trim()) {
      throw new Error(`Unsupported image format "${format}" requires a user hint`)
    }
    if (new Set(support.extensions).size !== support.extensions.length
      || support.extensions.some((extension) => !/^\.[a-z0-9]+$/.test(extension))) {
      throw new Error(`Image format "${format}" has invalid or duplicate extensions`)
    }
  }
  if (!IMAGE_FILE_ACCEPT.trim()) throw new Error('Supported image formats must generate a file-picker accept value')
}
